import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { useFilters } from '../contexts/FiltersContext';
import { useNavCounts } from '../hooks/useAnalyticsV2';
import { Badge } from './ui/Badge';

/**
 * The primary navigation.
 *
 * WHAT CHANGED AND WHY
 * --------------------
 * * **One accent, not twelve.** Each item used to carry its own hue from a
 *   12-recipe colour table, so colour meant nothing — it was decoration competing
 *   with the semantic green/amber/red the rest of the product uses for status.
 * * **Opaque.** The panel was translucent glass over an animated gradient, which
 *   left the desktop labels at roughly 1.3:1 against their background. A surface
 *   with a known contrast ratio is the whole reason the labels are now legible.
 * * **Badges.** An owner had to visit six pages to find out whether anything
 *   needed attention. The counts come from one cheap endpoint.
 * * **Performance is visible to agents.** It was owner-only, which left a rep
 *   with no reason to log in at all.
 */

const NAV_GROUPS = [
  {
    title: 'Main',
    items: [
      { path: '/', label: 'Overview', icon: 'fa-gauge-high', exact: true },
      { path: '/outlook', label: 'Outlook', icon: 'fa-wand-magic-sparkles' },
      { path: '/calls', label: 'Calls', icon: 'fa-phone-volume' },
      { path: '/intelligence', label: 'Intelligence', icon: 'fa-lightbulb' },
      { path: '/follow-ups', label: 'Follow-ups', icon: 'fa-list-check', badge: 'open_followups' },
      { path: '/contacts', label: 'Contacts', icon: 'fa-address-book' },
      { path: '/whatsapp', label: 'WhatsApp', icon: 'fa-brands fa-whatsapp', badge: 'unread_whatsapp' },
      { path: '/performance', label: 'Performance', icon: 'fa-chart-simple' },
    ],
  },
  {
    title: 'Workspace',
    ownerOnly: true,
    items: [
      { path: '/team', label: 'Team & access', icon: 'fa-user-group' },
      { path: '/settings', label: 'Settings', icon: 'fa-gear' },
    ],
  },
];

const NavItem = ({ item, isActive, badgeValue, onNavigate }) => {
  const current = isActive(item);
  return (
    <Link
      to={item.path}
      onClick={onNavigate}
      aria-current={current ? 'page' : undefined}
      className={[
        'group flex items-center gap-3 rounded-md px-3 py-2 text-label font-medium',
        'transition-colors duration-fast',
        current
          ? 'bg-brand-tint text-brand'
          : 'text-ink-700 hover:bg-surface-2 hover:text-ink-900',
      ].join(' ')}
    >
      <i
        className={`fas ${item.icon} w-4 shrink-0 text-center ${current ? 'text-brand' : 'text-ink-500 group-hover:text-ink-700'}`}
        aria-hidden="true"
      />
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      {/* A badge appears only for a real positive count. A zero is not news, and
          `null` means the count failed — which must show nothing rather than 0. */}
      {typeof badgeValue === 'number' && badgeValue > 0 ? (
        <Badge tone={item.badge === 'open_followups' ? 'warn' : 'brand'} size="sm">
          {badgeValue > 99 ? '99+' : badgeValue}
        </Badge>
      ) : null}
    </Link>
  );
};

const Sidebar = ({ isSidebarOpen, toggleSidebar }) => {
  const location = useLocation();
  const { isOwner, tenantName, isTrial } = useAuth();
  const { brandName, logoUrl } = useBranding();
  const { period } = useFilters();
  const { data: counts } = useNavCounts(period);

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
  };

  const closeOnMobile = () => {
    if (window.innerWidth < 1024 && isSidebarOpen) toggleSidebar();
  };

  return (
    <>
      {isSidebarOpen ? (
        <button
          type="button"
          aria-label="Close navigation"
          className="fixed inset-0 z-40 bg-ink-900/40 lg:hidden"
          onClick={toggleSidebar}
        />
      ) : null}

      <aside
        id="app-sidebar"
        aria-label="Primary"
        className={[
          'fixed inset-y-0 left-0 z-50 flex w-sidebar flex-col border-r border-line bg-surface',
          'sidebar-transition lg:translate-x-0',
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
      >
        <div className="flex items-center gap-3 border-b border-line px-4 py-3">
          <img
            src={logoUrl}
            alt=""
            className="h-8 w-8 shrink-0 rounded object-contain"
          />
          <div className="min-w-0 flex-1">
            <div className="truncate text-label font-semibold text-ink-900">
              {tenantName || brandName}
            </div>
            <div className="truncate text-micro text-ink-500">{brandName}</div>
          </div>
          {isTrial ? <Badge tone="warn" size="sm">Trial</Badge> : null}
        </div>

        <nav className="flex-1 overflow-y-auto p-2 thin-scrollbar">
          {NAV_GROUPS.map((group) => {
            if (group.ownerOnly && !isOwner) return null;
            return (
              <div key={group.title} className="mb-4">
                <h2 className="px-3 pb-1 pt-2 text-micro font-semibold uppercase tracking-wider text-ink-500">
                  {group.title}
                </h2>
                <div className="flex flex-col gap-0.5">
                  {group.items.map((item) => (
                    <NavItem
                      key={item.path}
                      item={item}
                      isActive={isActive}
                      badgeValue={item.badge ? counts?.[item.badge] : undefined}
                      onNavigate={closeOnMobile}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
